import { useState } from 'react';
import ListaMercatiModale from './ListaMercatiModale';
import { showDialogConfermaAggiuntaVenue, showDialogConfermaModifica, showDialogConfermaRimozioneVenue, showDialogSelectCategoria } from '../../../../../utils/dialog/showDialogFunction';
import ListaUnknownMercatiModale from './ListaUnknownMercatiModale';
import ListaMercatiGlobaliModale from '../../../../../utils/dialog/ListaMercatiGlobaliModale';
import { formatTitle } from '../../../../../utils/Format';
import { Mercato, TipoMercatoKey, Venue, SourceRead, AdesioneMercati } from '../../../../../api/apiTypes';
import Swal from 'sweetalert2';
import { updateAdesioneMercati, validaAdesioneMercati } from '../../../../../api/functionApi';
import { useAxiosInterceptor } from '../../../../../api/wrappers';
import ElementGenerationsPanel from '../../../ElementGenerationsPanel';
import EventList from '../../../../../utils/EventList';
import SourcesAccordion from '../../../../../components/report/SourcesAccordion';

type ContainerListaDriverPageProps = {
  intermediario: Mercato[]
  title: string;
  driverRoute: string;
  listaMercati: Venue[]
  id: string;
  driverId: string;
  intermediarioId: string;
  sources: SourceRead[]
  events: any[]
};

const tipiMercati: { key: TipoMercatoKey; label: string }[] = [
  { key: 'equity', label: 'Azioni' },
  { key: 'bonds', label: 'Obbligazioni' },
  { key: 'derivatives', label: 'Derivati' },
  { key: 'others', label: 'Altri mercati' },
  //  { key: 'undefined', label: 'Da assegnare' },
];

// Funzione centralizzata per il filtro delle VENUES standard
const getFilteredVenues = (
  intermediario: Mercato,
  tipoMercatoKey: TipoMercatoKey
): Venue[] => {
  const mercato = intermediario?.value?.[tipoMercatoKey];
  if (!mercato || !mercato.venues) return [];
  return mercato.venues;
};

// Funzione centralizzata per il filtro delle unknown_venues
const getFilteredUnknownVenues = (
  intermediario: Mercato,
  tipoMercatoKey: TipoMercatoKey
): string[] => {
  const mercato = intermediario?.value?.[tipoMercatoKey];
  return mercato?.unknown_venues || [];
};

// Funzione centralizzata per il filtro delle AMBIGUOUS VENUES
const getFilteredAmbiguousVenues = (
  intermediario: Mercato,
  tipoMercatoKey: TipoMercatoKey
): Venue[] => {
  const mercato = intermediario?.value?.[tipoMercatoKey];
  if (!mercato || !mercato.ambiguous_venues) return [];

  // Le ambiguous_venues sono un array di array, quindi usiamo flatMap per appiattirle
  const allAmbiguousVenues: Venue[] = mercato.ambiguous_venues.flatMap(
    (ambiguousGroup: Venue[]) => ambiguousGroup
  );

  // Il filtro basato sul CountryCode è stato rimosso
  return allAmbiguousVenues;
};

const ContainerAdesioneMercatiDettaglio = ({ intermediario, title, driverRoute, listaMercati, intermediarioId, id, driverId, sources, events }: ContainerListaDriverPageProps) => {
  useAxiosInterceptor(); // <--- monta l'interceptor
  const [localIntermediario, setLocalIntermediario] = useState<Mercato>(() => {
    const stored = localStorage.getItem(`intermediario_${driverRoute}_${intermediarioId}`);
    return stored ? JSON.parse(stored) : intermediario;
  });

  const [originalIntermediario, setOriginalIntermediario] = useState<Mercato | null>(null);

  const [validated, setValidated] = useState(localIntermediario.validation === "validated");
  const [isEditing, setIsEditing] = useState(false);
  const [modalData, setModalData] = useState<{
    tipoMercato: string;
    lista: Venue[];
    isAmbiguous: boolean;
  } | null>(null);

  const [unknownModalData, setUnknownModalData] = useState<{
    tipoMercato: string;
    lista: string[];
  } | null>(null);

  const [mercatiGlobaliModalData, setMercatiGlobaliModalData] = useState<{
    tipoMercato: string;
    lista: Venue[];
  } | null>(null);

const handleModalClick = (type: 'normal' | 'ambiguous' | 'unknown', key: TipoMercatoKey, label: string) => {
  if (!isEditing) return;

  switch (type) {
    case 'normal':
      const normalList = getFilteredVenues(localIntermediario, key);
      console.log('🔍 STRUTTURA VENUE:', normalList[0]); // Vedi la struttura
      console.log('🔍 TUTTE LE CHIAVI:', normalList[0] ? Object.keys(normalList[0]) : 'Lista vuota');
      setModalData({ tipoMercato: label, lista: normalList, isAmbiguous: false });
      break;
    case 'ambiguous':
      const ambiguousList = getFilteredAmbiguousVenues(localIntermediario, key);
      console.log('🔍 STRUTTURA VENUE (ambiguous):', ambiguousList[0]);
      setModalData({ tipoMercato: `${label} (Conflitti)`, lista: ambiguousList, isAmbiguous: true });
      break;
    case 'unknown':
      const unknownList = getFilteredUnknownVenues(localIntermediario, key);
      setUnknownModalData({ tipoMercato: `${label} (Sconosciuti)`, lista: unknownList });
      break;
    default:
      break;
  }
};

  const handleCloseModal = () => {
    setModalData(null);
  };

  const [values, setValues] = useState(() => ({
    equity: getFilteredVenues(localIntermediario, 'equity').length,
    bonds: getFilteredVenues(localIntermediario, 'bonds').length,
    derivatives: getFilteredVenues(localIntermediario, 'derivatives').length,
    others: getFilteredVenues(localIntermediario, 'others').length,
    undefined: getFilteredVenues(localIntermediario, 'undefined').length,
  }));

  const [ambiguousValues, setAmbiguousValues] = useState(() => ({
    equity: getFilteredAmbiguousVenues(localIntermediario, 'equity').length,
    bonds: getFilteredAmbiguousVenues(localIntermediario, 'bonds').length,
    derivatives: getFilteredAmbiguousVenues(localIntermediario, 'derivatives').length,
    others: getFilteredAmbiguousVenues(localIntermediario, 'others').length,
  }));

  const resetValues = (intermediario: Mercato) => {
    setValues({
      equity: getFilteredVenues(intermediario, 'equity').length,
      bonds: getFilteredVenues(intermediario, 'bonds').length,
      derivatives: getFilteredVenues(intermediario, 'derivatives').length,
      others: getFilteredVenues(intermediario, 'others').length,
      undefined: getFilteredVenues(intermediario, 'undefined').length,
    });

    setAmbiguousValues({
      equity: getFilteredAmbiguousVenues(intermediario, 'equity').length,
      bonds: getFilteredAmbiguousVenues(intermediario, 'bonds').length,
      derivatives: getFilteredAmbiguousVenues(intermediario, 'derivatives').length,
      others: getFilteredAmbiguousVenues(intermediario, 'others').length,
    });
  };

const handleAddVenue = (id?: string | number) => {
    const currentTipoMercato = modalData?.tipoMercato;
    if (modalData?.isAmbiguous && id !== undefined) {
      showDialogConfermaAggiuntaVenue(() => {
        const updatedIntermediario = { ...localIntermediario };
        const currentTipoMercatoKey = tipiMercati.find(t => currentTipoMercato?.startsWith(t.label))?.key;

        if (currentTipoMercatoKey && updatedIntermediario.value) {
          const mercato = updatedIntermediario.value[currentTipoMercatoKey];
          if (mercato) {
            let venueToMove: Venue | undefined;
           mercato.ambiguous_venues = mercato.ambiguous_venues
  .map(group => {
    const filteredGroup = group.filter(v => {
      // Se l'id corrisponde a quello cliccato, lo spostiamo
      if (v.id === id) {
        venueToMove = v;
        return false; // lo rimuovo dal gruppo ambiguo
      }
      return true; // gli altri rimangono
    });
    return filteredGroup;
  })
  .filter(group => group.length > 0);

            if (venueToMove) {
              if (!mercato.venues) {
                mercato.venues = [];
              }
              mercato.venues.push(venueToMove);
            }
          }
        }
        setLocalIntermediario(updatedIntermediario);
        localStorage.setItem(`intermediario_${driverRoute}_${updatedIntermediario.id}`, JSON.stringify(updatedIntermediario));
        // passa l'oggetto aggiornato
        resetValues(updatedIntermediario);
        handleCloseModal();
      });
    } else if (currentTipoMercato === 'Da assegnare' || currentTipoMercato?.startsWith('Da assegnare')) {
      showDialogSelectCategoria(
        (categoriaSelezionata) => {
          const updatedIntermediario = { ...localIntermediario };
          const undefinedMercato = updatedIntermediario.value?.undefined;
          if (!undefinedMercato) {
            console.warn("Mercato 'undefined' non trovato.");
            handleCloseModal();
            return;
          }
          const venueToMove = undefinedMercato.venues?.find(v => v.id === id);
          if (!id || !venueToMove) {
            alert("Venue non trovata o ID mancante.");
            handleCloseModal();
            return;
          }
          undefinedMercato.venues = undefinedMercato.venues.filter(v => v.id );
          const validCategories: TipoMercatoKey[] = ['equity', 'bonds', 'derivatives', 'others', 'undefined'];
          if (!validCategories.includes(categoriaSelezionata as TipoMercatoKey)) {
            alert('Categoria selezionata non valida');
            handleCloseModal();
            return;
          }
          const cat = categoriaSelezionata as TipoMercatoKey;
          if (!updatedIntermediario.value[cat]) {
            updatedIntermediario.value[cat] = {
              counter: 0,
              warning: false,
              venues: [],
              ambiguous_venues: [],
              unknown_venues: [],
            };
          }
          updatedIntermediario.value[cat]!.venues.push(venueToMove);
          setLocalIntermediario(updatedIntermediario);
          localStorage.setItem(
            `intermediario_${driverRoute}_${updatedIntermediario.id}`,
            JSON.stringify(updatedIntermediario)
          );
          // Qui devi passare l'oggetto aggiornato
          resetValues(updatedIntermediario);
          handleCloseModal();
        },
        () => {
          handleCloseModal();
        }
      );
    } else {
      const tipoBase = tipiMercati.find((t) => currentTipoMercato?.startsWith(t.label));
      const titoloModale = tipoBase ? `${tipoBase.label} (Tutti)` : 'Tutti i Mercati';
      setMercatiGlobaliModalData({
        tipoMercato: titoloModale,
        lista: listaMercati,
      });
    }
  };
  const handleAddFromUnknown = (venue: Venue, tipoMercatoLabel: string) => {
    const updatedIntermediario = { ...localIntermediario };

    const currentTipoMercatoKey = tipiMercati.find((t) =>
      tipoMercatoLabel.startsWith(t.label)
    )?.key;

    if (currentTipoMercatoKey && updatedIntermediario.value) {
      const mercato = updatedIntermediario.value[currentTipoMercatoKey];
      if (mercato) {
        // Rimuovi dall’unknown
        mercato.unknown_venues = mercato.unknown_venues.filter(
          (u) => u !== venue.market_name
        );

        // Aggiungi alla lista venues
        if (!mercato.venues) {
          mercato.venues = [];
        }
        mercato.venues.push(venue);
      }
    }

    // Aggiorna lo stato locale e localStorage
    setLocalIntermediario(updatedIntermediario);
    localStorage.setItem(
      `intermediario_${driverRoute}_${updatedIntermediario.id}`,
      JSON.stringify(updatedIntermediario)
    );

    // Aggiorna lista modale
    if (unknownModalData) {
      setUnknownModalData({
        ...unknownModalData,
        lista: unknownModalData.lista.filter((v) => v !== venue.market_name),
      });
    }

    resetValues(updatedIntermediario);

    // Mostra subito il messaggio di successo
    Swal.fire({
      icon: "success",
      title: "Aggiunto!",
      text: `"${venue.market_name}" è stato aggiunto con successo.`,
      confirmButtonColor: "#374151",
    });
  };

  const handleAddToModalList = (venue: Venue): Promise<boolean> => {
    return new Promise((resolve) => {
      Swal.fire({
        title: 'Sei sicuro?',
        text: `Vuoi aggiungere ${venue.market_name} alla lista?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sì, aggiungi!',
        cancelButtonText: 'Annulla',
        reverseButtons: true,
        customClass: {
          confirmButton:
            "bg-textPrimary text-white hover:bg-textSecondary px-4 py-2 rounded ml-2",
          cancelButton:
            "bg-textSecondary text-white hover:bg-textPrimary px-4 py-2 rounded",
          actions: "flex justify-between gap-2",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          // Se l'utente conferma, procedi con la logica di aggiunta
          const updatedIntermediario = { ...localIntermediario };
          const currentTipoMercatoKey = tipiMercati.find(t => mercatiGlobaliModalData?.tipoMercato.startsWith(t.label))?.key;

          if (currentTipoMercatoKey && updatedIntermediario.value) {
            const mercato = updatedIntermediario.value[currentTipoMercatoKey];
            if (mercato) {
              if (!mercato.venues) {
                mercato.venues = [];
              }
              mercato.venues.push(venue);
            }
          }

          setLocalIntermediario(updatedIntermediario);
          localStorage.setItem(`intermediario_${driverRoute}_${updatedIntermediario.id}`, JSON.stringify(updatedIntermediario));
          resetValues(updatedIntermediario);

          Swal.fire({
            title: 'Aggiunto!',
            text: `${venue.market_name} è stato aggiunto.`,
            icon: 'success',
            confirmButtonColor: '#374151'
          });

          resolve(true); // Risolve la promise con true in caso di successo
        } else {
          resolve(false); // Risolve con false se l'utente annulla
        }
      });
    });
  };

const handleRemoveVenue = (id: number | string | undefined) => {
  
  showDialogConfermaRimozioneVenue(() => {
    const updatedIntermediario = { ...localIntermediario };
    const currentTipoMercatoKey = tipiMercati.find(t =>
      modalData?.tipoMercato.startsWith(t.label)
    )?.key;

    if (currentTipoMercatoKey && updatedIntermediario.value) {
      const mercato = updatedIntermediario.value[currentTipoMercatoKey];
      if (mercato) {
        // ✅ Prova con diversi identificatori
        mercato.ambiguous_venues = mercato.ambiguous_venues
          .map(group => group.filter(v => {
            // Confronta tutti i possibili campi ID
            const matches = 
              v.id === id || 
              (v as any).id === id ||
              v.market_name === id; // Fallback: usa il nome
            
            if (matches) {
              console.log('✅ MATCH TROVATO:', v.market_name, {
                id: v.id,
              });
            }
            return !matches;
          }))
          .filter(group => group.length > 0);
        
        mercato.venues = mercato.venues?.filter(v => {
          const matches = 
            v.id === id || 
            (v as any).id === id ||
            v.market_name === id;
          
          if (matches) {
            console.log('✅ MATCH TROVATO in venues:', v.market_name, {
              id: v.id,
            });
          }
          return !matches;
        }) || [];
      }
    }

    setLocalIntermediario(updatedIntermediario);
    localStorage.setItem(
      `intermediario_${driverRoute}_${updatedIntermediario.id}`,
      JSON.stringify(updatedIntermediario)
    );
    resetValues(updatedIntermediario);

    if (modalData && currentTipoMercatoKey) {
      const isAmbiguous = modalData.isAmbiguous;
      const nuovaLista = isAmbiguous 
        ? getFilteredAmbiguousVenues(updatedIntermediario, currentTipoMercatoKey)
        : getFilteredVenues(updatedIntermediario, currentTipoMercatoKey);
      
      setModalData({
        ...modalData,
        lista: nuovaLista
      });
    }
  });
};

  const handleRemoveUnknownVenue = (item: string, tipoMercatoLabel: string) => {
    showDialogConfermaRimozioneVenue(() => {
      const updatedIntermediario = { ...localIntermediario };

      const currentTipoMercatoKey = tipiMercati.find(t =>
        tipoMercatoLabel.startsWith(t.label)
      )?.key;

      if (currentTipoMercatoKey && updatedIntermediario.value) {
        const mercato = updatedIntermediario.value[currentTipoMercatoKey];
        if (mercato?.unknown_venues) {
          mercato.unknown_venues = mercato.unknown_venues.filter(v => v !== item);
        }
      }

      setLocalIntermediario(updatedIntermediario);
      localStorage.setItem(
        `intermediario_${driverRoute}_${updatedIntermediario.id}`,
        JSON.stringify(updatedIntermediario)
      );

      // aggiorna la lista visibile nella modale
      if (unknownModalData) {
        setUnknownModalData({
          ...unknownModalData,
          lista: unknownModalData.lista.filter(v => v !== item),
        });
      }
    });
  };

  const handleModifica = () => {
    showDialogConfermaModifica(
      () => {
        setOriginalIntermediario(JSON.parse(JSON.stringify(localIntermediario)));
        setIsEditing(true);
      },
    );
  };

  const handleValidazione = async () => {
    // Mostra un popup di conferma all'utente prima di procedere
    const result = await Swal.fire({
      title: 'Sei sicuro?',
      text: 'Vuoi procedere con la validazione ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#374151',
      cancelButtonColor: '#9CA3AF',
      confirmButtonText: 'Sì, valida!',
      cancelButtonText: 'Annulla'
    });

    if (!result.isConfirmed) return;

  // Mostra Swal di caricamento
  Swal.fire({
    title: 'Validazione in corso...',
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => {
      Swal.showLoading();
    }
  });

    try {
      // Chiama la funzione per validare l'indice di penetrazione
      await validaAdesioneMercati(id, driverId, intermediarioId);

      // Aggiorna lo stato per riflettere la validazione completata
      setValidated(true);
      setIsEditing(false); // La validazione completa il processo di modifica

      // Mostra un popup di successo all'utente
      Swal.fire({
        icon: 'success',
        title: 'Validazione completata!',
        text: 'Adesione Mercato validata correttamente.',
        confirmButtonColor: '#374151',
      });
    } catch (error) {
      // Logga l'errore per il debug
      console.error('Errore durante la validazione:', error);

      // Mostra un popup di errore all'utente
      Swal.fire({
        icon: 'error',
        title: 'Errore nella validazione',
        text: 'Si è verificato un errore durante la validazione.',
        confirmButtonColor: '#374151',
      });
    }
  };

  
const mapVenueToBackendFormat = (venue: Venue) => ({
  id: venue.id ? String(venue.id) : "", 
  market_name: venue.market_name || "",
  mic: venue.mic || "",
  operating_mic: venue.operating_mic || "",
  legal_entity_name: venue.legal_entity_name || "",
  acronym: venue.acronym || "",
  status: venue.status || "",
  validated: venue.validated ?? false,
  iso_country_code: venue.CountryCode || "", 
  city: venue.City || "", 
  market_category: venue.MarketCategory || ""
});

const mapAdesioneMercatiToBackend = (value: AdesioneMercati): any => {
  const mappedValue: any = {
  };

  const marketTypes: TipoMercatoKey[] = ['equity', 'bonds', 'derivatives', 'others', 'undefined'];
  
  marketTypes.forEach(type => {
    if (value[type]) {
      mappedValue[type] = {
        counter: value[type]!.counter,
        warning: value[type]!.warning,
        venues: value[type]!.venues.map(mapVenueToBackendFormat),
        ambiguous_venues: value[type]!.ambiguous_venues.map(group => 
          group.map(mapVenueToBackendFormat)
        ),
        unknown_venues: value[type]!.unknown_venues
      };
    }
  });

  return mappedValue;
};

const handleSalva = async () => {
  const result = await Swal.fire({
    title: 'Sei sicuro?',
    text: 'Vuoi procedere con il salvataggio?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: "#374151",
    cancelButtonColor: "#9CA3AF",
    reverseButtons: true,
    customClass: {
      confirmButton:
        "bg-textPrimary text-white hover:bg-textSecondary px-4 py-2 rounded ml-2",
      cancelButton:
        "bg-textSecondary text-white hover:bg-textPrimary px-4 py-2 rounded",
      actions: "flex justify-between gap-2",
    },
  });

  if (!result.isConfirmed) return;

  try {
    // ✅ Mappa i dati al formato del backend prima di inviare
    const mappedValue = mapAdesioneMercatiToBackend(localIntermediario.value);
    
    await updateAdesioneMercati(id, driverId, intermediarioId, { value: mappedValue });

    setOriginalIntermediario(null);
    setIsEditing(false);
    setValidated(false);

    Swal.fire({
      icon: 'success',
      title: 'Modifiche salvate!',
      text: 'Le modifiche sono state salvate correttamente.',
      confirmButtonColor: '#374151',
    });
  } catch (error) {
    console.error('Errore durante il salvataggio:', error);
    Swal.fire({
      icon: 'error',
      title: 'Errore nel salvataggio',
      text: 'Si è verificato un errore durante il salvataggio delle modifiche.',
      confirmButtonColor: '#374151',
    });
  }
};


  const handleAnnulla = () => {
    if (originalIntermediario) {
      setLocalIntermediario(originalIntermediario);
      resetValues(originalIntermediario); // Passa l'intermediario originale
    }
    setOriginalIntermediario(null);
    setIsEditing(false);
  };

  const formattedTitle = formatTitle(title);

  return (
    <div>
      <div className="bg-white rounded-xl border border-gray-200 p-8 ">
        <h2 className="text-4xl font-bold text-[#1B365D] mb-4">{formattedTitle}</h2>
        <h4 className="text-2xl font-bold text-[#1B365D] mb-8">{localIntermediario.name}</h4>

        {isEditing && (
          <p className="text-base text-gray-600 mb-6">
            Seleziona il mercato di riferimento per modificare i dati.
          </p>
        )}
        <div className="grid grid-cols-2 gap-4">
          {tipiMercati
            .filter(({ key }) => !(key === 'undefined' && values.undefined < 1))
            .map(({ key, label }) => (
              <div
                key={key}
                className="flex items-center justify-between bg-gray-50 rounded-xl border border-gray-100 p-4"
              >
                <span className="block text-sm font-medium text-gray-600 mb-1">{label}</span>
                <div className="flex space-x-2">
                  {getFilteredUnknownVenues(localIntermediario, key).length > 0 && (
                    <button
                      disabled={!isEditing}
                      className={`px-3 py-1 rounded ${isEditing
                        ? 'bg-red-300 hover:bg-red-400 cursor-pointer'
                        : 'bg-red-100 cursor-not-allowed'
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleModalClick('unknown', key, label);
                      }}
                    >
                      {getFilteredUnknownVenues(localIntermediario, key).length}
                    </button>
                  )}
                  {getFilteredAmbiguousVenues(localIntermediario, key).length > 0 && (
                    <button
                      disabled={!isEditing}
                      className={`px-3 py-1 rounded ${isEditing
                        ? 'bg-yellow-200 hover:bg-yellow-300 cursor-pointer'
                        : 'bg-yellow-100 cursor-not-allowed'
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleModalClick('ambiguous', key, label);
                      }}
                    >
                      {getFilteredAmbiguousVenues(localIntermediario, key).length}
                    </button>
                  )}
                  <button
                    disabled={!isEditing}
                    className={`px-3 py-1 rounded ${isEditing
                        ? (key === 'undefined'
                          ? 'bg-red-300 hover:bg-red-400 cursor-pointer'
                          : 'bg-gray-200 hover:bg-gray-300 cursor-pointer')
                        : (key === 'undefined'
                          ? 'bg-red-100 cursor-not-allowed'
                          : 'bg-gray-100 cursor-not-allowed')
                      }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleModalClick('normal', key, label);
                    }}
                  >
                    {values[key]}
                  </button>
                </div>
              </div>
            ))}
        </div>
{/* Mostra i pulsanti solo se report_status NON è approved/drafted/completed */}
{!['approved', 'drafted', 'completed'].includes(localIntermediario.report_status ?? '') && (
  <div className="flex justify-end space-x-4 mt-6">
    {!isEditing ? (
      <>
        <button
          onClick={handleModifica}
          className="px-4 py-2 bg-[#1B365D] text-white hover:bg-[#142a4a] rounded-lg"
        >
          Modifica
        </button>
        <button
          onClick={handleValidazione}
          disabled={validated} // <--- Disabilita se già validato
          className={`px-4 py-2 rounded 
            ${validated 
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
              : 'bg-textPrimary text-white hover:bg-textSecondary'}`
          }
        >
          Valida
        </button>
      </>
    ) : (
      <>
        <button
          onClick={handleAnnulla}
          className="px-4 py-2 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded"
        >
          Annulla
        </button>
        <button
          onClick={handleSalva}
          className="px-4 py-2 bg-[#1B365D] text-white hover:bg-[#142a4a] rounded-lg"
        >
          Salva
        </button>
      </>
    )}
  </div>
)}

        <div>
          <p className='mb-1'>   I colori indicano lo stato dei mercati :</p>
          <p className='ml-2'>   * grigio per i verificati </p>
          <p className='ml-2'>   * giallo per quelli con conflitti </p>
          <p className='ml-2'>   * rosso per quelli sconosciuti.</p>
        </div>
      </div>
      <SourcesAccordion sources={sources} reportId={id} componentId={driverId} elementId={intermediarioId} />
            {/* Sezione Attività */}
<EventList events={events} />
      <ElementGenerationsPanel reportId={id} componentId={driverId} elementId={intermediarioId} />


      {modalData && (
        <ListaMercatiModale
          isOpen={!!modalData}
          onClose={handleCloseModal}
          title={modalData.tipoMercato}
          lista={modalData.lista}
          onAdd={handleAddVenue}
          onRemove={handleRemoveVenue}
          isEditing={isEditing}
          isAmbiguousList={modalData.isAmbiguous}
        />
      )}
      {unknownModalData && (
        <ListaUnknownMercatiModale
          isOpen={!!unknownModalData}
          onClose={() => setUnknownModalData(null)}
          title={unknownModalData.tipoMercato}
          lista={unknownModalData.lista}
          onRemove={(item) => handleRemoveUnknownVenue(item, unknownModalData.tipoMercato)}
          listaMercati={listaMercati}
          onAddToVenueList={(venue) => handleAddFromUnknown(venue, unknownModalData.tipoMercato)}

        />
      )}
      {mercatiGlobaliModalData && (
        <ListaMercatiGlobaliModale
          isOpen={!!mercatiGlobaliModalData}
          onClose={() => setMercatiGlobaliModalData(null)}
          title={mercatiGlobaliModalData.tipoMercato}
          lista={mercatiGlobaliModalData.lista}
          onAddToModalList={handleAddToModalList}
        />
      )}
    </div>
  );
};

export default ContainerAdesioneMercatiDettaglio;